import { Heading, AspectRatio, Flex, Box, Text } from '@chakra-ui/react';
import { css } from '@emotion/react';
import VimeoPlayer from '@vimeo/player';
import { FC, useEffect } from 'react';
import { useTranslation } from 'react-i18next';

import Arrow from '@/assets/icons/ic-arrow.svg';

import useElement from '@/hooks/useElement';
import { ButtonTheme } from '@/styles/themes/button';

import { GlassButton } from '../core/Button/variants/GlassButton';

const buttonIconCss = css`
  margin-left: 8px;
  transform: rotate(90deg);
  width: 16px;
  height: 16px;
`;

const buttonStyles = css`
  @media (min-width: 30em) and (max-width: 62em) {
    align-self: center;
  }
`;

interface IHeaderProps {
  onLearnMoreClick: () => void;
}

export const Header: FC<IHeaderProps> = ({ onLearnMoreClick }) => {
  const { t } = useTranslation();
  const { element: vimeoVideoElement, ref: videoRef } = useElement();

  useEffect(() => {
    if (!vimeoVideoElement) {
      return;
    }
    // eslint-disable-next-line no-new
    new VimeoPlayer(vimeoVideoElement);
  }, [vimeoVideoElement]);

  // Box compensates for the bad navigation design with padding and negative margins
  // to pull it under the navigation and spread the gradient all over the hero section
  return (
    <Box
      bgImage="radial-gradient(
        98.44% 95.14% at -0.8% 0%,
        #f563ff 0%,
        rgba(8, 255, 255, 0.5) 10.76%,
        rgba(15, 255, 231, 0.35) 30%,
        rgba(255, 120, 147, 0.1) 55%,
        rgba(0, 5, 15, 0) 86%
      ), linear-gradient(#000, #000)"
      bgSize={['cover, 100% 85%', 'cover, 100% 80%', 'cover, 100% 75%', 'cover, cover']}
      bgRepeat="no-repeat"
      mt={['-104px', null, '-64px']}
      p={['104px 20px 0', null, '64px 40px 0', '64px 40px 80px', '64px 0px 80px']}
    >
      <Flex
        flexDirection={['column', null, null, 'row']}
        textAlign={[null, 'center', null, 'initial']}
        maxW="75rem"
        mx="auto"
        mt={[10, null, null, 20]}
      >
        <Box maxW={['full', null, null, '40%']} alignSelf={[null, null, null, 'center']}>
          <Heading
            as="h1"
            color="white"
            fontSize={['4xl', null, null, '5xl']}
            lineHeight="shorter"
            mb={[10, null, null, 12]}
          >
            {t('landing.headerTitle')}
          </Heading>
          <Text color="white" fontSize="30px" fontWeight="bold" mb={[10, null, null, 12]}>
            {t('landing.headerSubtitle')}
          </Text>
          {/* TODO: refactor + make it a link to scroll to next section instead of click handler */}
          <GlassButton buttonTheme={ButtonTheme.DARK} onClick={onLearnMoreClick} css={buttonStyles}>
            {t('landing.learnMoreButton')}
            <Arrow css={buttonIconCss} />
          </GlassButton>
        </Box>

        <AspectRatio maxW="full" ratio={16 / 9} mt={[16, null, null, 0]} ml={[null, null, null, 16]} flexGrow={1}>
          <iframe
            ref={videoRef}
            src="https://player.vimeo.com/video/811138682"
            frameBorder="0"
            allow="fullscreen; picture-in-picture"
            width="100%"
            height="100%"
            title="Underline Event Organisation Demo"
          />
        </AspectRatio>
      </Flex>
    </Box>
  );
};
